import useAxios from 'axios-hooks';
import React from 'react';
import axios, {
    AxiosError,
    AxiosRequestConfig,
    AxiosResponseTransformer,
} from 'axios';
import { usePrevious } from 'react-use';
import isEqual from 'lodash/isEqual';
import { useAuth0Context } from '../auth/auth0/Auth0Context';
import { xconsole } from '../utilities/utilities';

export interface IApiConfig extends AxiosRequestConfig {
    isLocalRequest?: boolean;
    useAuth?: boolean;
    executeType?: 'automatic' | 'manual' | 'onFirstRender';
    useCache?: boolean;
}

const apiConfigDefaults: IApiConfig = {
    isLocalRequest: false,
    useAuth: true,
    executeType: 'automatic',
    useCache: true,
};

const transformApiConfig: (
    apiConfig: IApiConfig,
    accessToken?: string,
) => AxiosRequestConfig = (apiConfig, accessToken) => {
    const result: AxiosRequestConfig = { ...apiConfig };

    if (apiConfig.useAuth) {
        if (!result.headers) result.headers = {};
        result.headers.Authorization = `Bearer ${accessToken}`;
    }

    if (apiConfig.transformResponse) {
        result.transformResponse = (
            axios.defaults.transformResponse as AxiosResponseTransformer[]
        ).concat(
            Array.isArray(apiConfig.transformResponse)
                ? apiConfig.transformResponse
                : [apiConfig.transformResponse],
        );
    }
    return result;
};

export const useApi = <T>(config: IApiConfig) => {
    /**
     * Beware at how this thing works:
     * success state = data && ! loading && ! error
     * fail = error && ! loading
     *
     * that's because --
     * 1st request X succeeds, you get [Xdata, false, null]
     * 2nd request Y is in progress, you get [Xdata, true, null]
     * 2nd request Y fails, you get [Xdata, true, Yerror]
     * 3rd request Z is in progress, you get [Xdata, true, null]
     * 3rd request Z succeeds, you get [Zdata, false, null]
     *
     * it kinda makes sense if you have some sort of "refresh" functionality
     * where you want to keep the state of the last success alive
     * in other cases it's not very intuitive
     *
     * Also beware, endpoints which don't actually return any data (and e.g. 204 No Content)
     * are currently very painful to deal with and not recommended to use.
     */

    const apiConfig = React.useMemo(
        () => ({ ...apiConfigDefaults, ...config }),
        [config],
    );

    const { accessToken } = useAuth0Context();
    const executeType = apiConfig.executeType || 'automatic';

    const [executed, setExecuted] = React.useState(false);

    const axiosConfig = React.useMemo(
        () => transformApiConfig(apiConfig, accessToken),
        [accessToken, apiConfig],
    );

    const [responseValues, executeRequest] = useAxios<T>(
        {},
        {
            // Setting manual to false seems to cause several problems
            // it seems better to keep it manual and simulate automatic via a useEffect
            manual: true,
            useCache: apiConfig.useCache,
        },
    );

    const requestBeforeResetRef = React.useRef<XMLHttpRequest>();

    const [hookResponseValues, setHookResponseValues] = React.useState<{
        data?: T;
        loading: boolean;
        error: AxiosError<any> | null;
    }>({
        data: undefined,
        loading: false,
        error: null,
    });

    React.useEffect(() => {
        const latestRequest =
            responseValues.error?.request || responseValues.response?.request;
        if (
            // after reset() is called and axios re-executes, one tick has the same request as before reset
            requestBeforeResetRef.current === undefined ||
            requestBeforeResetRef.current !== latestRequest
        ) {
            requestBeforeResetRef.current = undefined;
            setHookResponseValues(responseValues);
        }
    }, [responseValues]);

    const [loading, setLoading] = React.useState(executeType !== 'manual');

    React.useEffect(() => {
        if (!executed && executeType === 'onFirstRender') {
            setExecuted(true);
            setLoading(true);
            executeRequest(axiosConfig).catch(() => {});
        }
    }, [executed, executeRequest, executeType, axiosConfig]);

    const previousData = usePrevious(axiosConfig.data);
    const previousUrl = usePrevious(axiosConfig.url);

    React.useEffect(() => {
        if (
            executeType === 'automatic' &&
            (!isEqual(previousData, axiosConfig.data) ||
                previousUrl !== axiosConfig.url)
        ) {
            setExecuted(true);
            setLoading(true);
            executeRequest(axiosConfig).catch(() => {});
        }
    }, [executeRequest, executeType, axiosConfig, previousData, previousUrl]);

    // Axios loading state seems to switch to 'true' way too late, which causes some issues
    // that's why we're using our own loading state and only watching the axios loading to go true -> false
    const previousAxiosLoading = usePrevious(responseValues.loading);
    React.useEffect(() => {
        if (previousAxiosLoading && !responseValues.loading) {
            setLoading(false);
        }
    }, [responseValues.loading, previousAxiosLoading]);

    const execute = React.useCallback(
        (newConfig?: AxiosRequestConfig) => {
            setExecuted(true);
            setLoading(true);
            if (!newConfig) executeRequest(axiosConfig).catch(() => {});
            else
                executeRequest(
                    transformApiConfig(
                        { ...apiConfig, ...newConfig },
                        accessToken,
                    ),
                ).catch(() => {});
        },
        [executeRequest, accessToken, apiConfig, axiosConfig],
    );

    const returnValues = React.useMemo(
        () => ({
            ...hookResponseValues,
            loading,
            reset: () => {
                if (loading) {
                    xconsole.warn(
                        `useApi hook was reset during loading state 
                    (data will be overridden once request is finished)`,
                    );
                }

                requestBeforeResetRef.current =
                    responseValues.error?.request ||
                    responseValues.response?.request;

                setHookResponseValues({
                    data: undefined,
                    loading: false,
                    error: null,
                });
            },
            execute,
        }),
        [
            hookResponseValues,
            loading,
            execute,
            responseValues.error?.request,
            responseValues.response?.request,
        ],
    );

    return returnValues;
};
